<template>
  <div class="row g-6">
    <div class="col-12">
      <div class="d-flex align-items-center mb-4">
        <button class="btn btn-primary me-3" @click="runStrategy" :disabled="isRunning">
          <i class="ti ti-player-play ti-sm me-1"></i>
          Запустить стратегию
        </button>
      </div>

      <div v-if="isRunning" class="alert alert-info">
        <div class="d-flex align-items-center">
          <i class="ti ti-loader ti-spin ti-sm me-2"></i>
          <span>Стратегия запущена в фоновом режиме...</span>
        </div>
      </div>
      
      <div v-if="logs.length > 0" class="mt-5">
        <h6 class="mb-3">История запусков</h6>
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>ДАТА</th>
                <th>СТАТУС</th>
                <th>СООБЩЕНИЕ</th>
                <th style="text-align: right">ОТЧЕТ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id">
                <td>{{ formatDate(log.run_time) }}</td>
                <td>
                  <span :class="getStatusClass(log.result?.status)">
                    {{ getStatusText(log.result?.status) }}
                  </span>
                </td>
                <td>
                  <template v-if="!log.result || isEmptyResult(log.result)">
                    <span class="text-muted">—</span>
                  </template>
                  <template v-else-if="isNewResultFormat(log.result)">
                    {{ formatNewResult(log.result) }}
                    <i
                      v-if="log.result.skipped > 0"
                      class="ti ti-info-circle ms-1"
                      v-tooltip.bottom="'Заблокированы — есть в других приоритетных стратегиях. Пропущены — нет данных для расчётов или изменений не было.'"
                      style="cursor: pointer;"
                    ></i>
                  </template>
                  <template v-else-if="log.result.updated_offers !== undefined">
                    Обновлено офферов: {{ log.result.updated_offers }}
                    <template v-if="log.result.skipped_offers !== undefined && log.result.skipped_offers > 0">
                      , пропущено: {{ log.result.skipped_offers }}
                      <i
                        class="ti ti-info-circle ms-1"
                        v-tooltip.bottom="'Офферы могли быть заблокированы, если они уже есть в других более приоритетных стратегиях.'"
                        style="cursor: pointer;"
                      ></i>
                    </template>
                  </template>
                  <template v-else>
                    {{ log.message || JSON.stringify(log.result) }}
                  </template>
                </td>
                <td style="text-align: right">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="downloadLogReport(log)"
                    :disabled="downloadingLogId === log.id"
                  >
                    <i class="ti ti-download ti-sm me-1"></i>
                    {{ downloadingLogId === log.id ? 'Скачивание...' : 'Скачать CSV' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="row">
          <div class="col-sm-4">
            <div class="mt-3" v-show="paginationInfo.total > 0">
              Логов на странице:
              <select :value="paginationInfo.size" @change="changePageSize" class="form-select form-select-sm w-auto d-inline-block ms-2">
                <option v-for="size in pageSizes" :key="size" :value="size">{{ size }}</option>
              </select>
            </div>
          </div>
          <div class="col-sm-4">
            <BasePagination :info="paginationInfo" @changePage="changePage" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { runStrategy, getStrategyLogs, downloadStrategyLogDetailsCsv } from '@/api/strategies'
import { Message, Messages } from '@/data/messages'
import BasePagination from '@/components/list/Pagination.vue'
import Tooltip from 'primevue/tooltip'
import { user } from '@/data/user'

export default {
  name: 'StrategyRun',
  components: {
    BasePagination
  },
  directives: {
    tooltip: Tooltip
  },
  props: {
    strategyId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isRunning: false,
      logs: [],
      paginationInfo: {},
      currentPage: 1,
      pageSize: 10,
      pageSizes: [10, 20, 50, 100],
      logsLoading: false,
      controlMinPrice: false,
      companyTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      logsRefreshInterval: null,
      downloadingLogId: null
    }
  },
  methods: {
    async runStrategy() {
      this.isRunning = true
      this.stopLogsRefresh()
      try {
        const result = await runStrategy(this.strategyId)
        if (result.status === 200) {
          const message = new Message()
          message.text = 'Стратегия успешно запущена в фоновом режиме'
          message.type = 'success'
          message.autoremove = 10
          Messages.list.push(message)
          await this.loadLogs()
          this.startLogsRefresh()
        }
      } catch (error) {
        const message = new Message()
        message.type = 'failure'
        message.text = 'Ошибка при запуске стратегии: ' + (error.response?.data?.message || 'Неизвестная ошибка')
        Messages.list.push(message)
        this.isRunning = false
      }
    },
    startLogsRefresh() {
      this.stopLogsRefresh()
      const intervalMs = 4000
      this.logsRefreshInterval = setInterval(async () => {
        await this.loadLogs()
        const latest = this.logs[0]
        const status = latest?.result?.status
        if (status === 'success' || status === 'error') {
          this.stopLogsRefresh()
          this.isRunning = false
        }
      }, intervalMs)
    },
    stopLogsRefresh() {
      if (this.logsRefreshInterval) {
        clearInterval(this.logsRefreshInterval)
        this.logsRefreshInterval = null
      }
    },
    async loadLogs() {
      this.logsLoading = true
      try {
        const result = await getStrategyLogs(this.strategyId, this.currentPage, this.pageSize)
        if (result.status === 200) {
          this.logs = result.data.items
          this.paginationInfo = {
            total: result.data.total,
            pages: result.data.pages,
            page: result.data.page,
            size: result.data.size
          }
        }
      } catch (error) {
        console.error('Ошибка при загрузке логов:', error)
      }
      this.logsLoading = false
    },
    async changePage(page) {
      this.currentPage = page
      await this.loadLogs()
    },
    async changePageSize(event) {
      this.pageSize = Number(event.target.value)
      this.currentPage = 1
      await this.loadLogs()
    },
    getFilenameFromDisposition(contentDisposition, fallbackName) {
      if (!contentDisposition) return fallbackName
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
      if (utf8Match && utf8Match[1]) {
        return decodeURIComponent(utf8Match[1]).replace(/["']/g, '')
      }
      const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
      if (simpleMatch && simpleMatch[1]) {
        return simpleMatch[1]
      }
      return fallbackName
    },
    async downloadLogReport(log) {
      if (!log?.id) return
      this.downloadingLogId = log.id
      try {
        const response = await downloadStrategyLogDetailsCsv(log.id)
        const fallbackName = `strategy-log-${log.id}.csv`
        const filename = this.getFilenameFromDisposition(
          response.headers?.['content-disposition'],
          fallbackName
        )
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        const message = new Message()
        message.type = 'failure'
        message.text = 'Не удалось скачать отчет запуска: ' + (error.response?.data?.message || 'Неизвестная ошибка')
        Messages.list.push(message)
      } finally {
        this.downloadingLogId = null
      }
    },
    formatDate(date) {
      if (!date) {
        return '';
      }
      try {
        const d = new Date(date + 'Z');
        return new Intl.DateTimeFormat('ru-RU', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
          timeZone: this.companyTimezone
        }).format(d);
      } catch (e) {
        console.error('Ошибка форматирования даты:', e, 'timezone:', this.companyTimezone, 'date:', date);
        const d = new Date(date + 'Z');
        return d.toLocaleString('ru-RU');
      }
    },
    getStatusClass(status) {
      const classes = {
        success: 'badge bg-success',
        error: 'badge bg-danger',
        running: 'badge bg-info',
        pending: 'badge bg-warning'
      }
      return classes[status] || 'badge bg-secondary'
    },
    getStatusText(status) {
      const texts = {
        success: 'Успешно',
        error: 'Ошибка',
        running: 'Выполняется',
        pending: 'Ожидает'
      }
      return texts[status] || status
    },
    isEmptyResult(result) {
      if (!result || typeof result !== 'object') return true
      return Object.keys(result).length === 0
    },
    isNewResultFormat(result) {
      return result && typeof result === 'object' && 'total' in result && 'success' in result
    },
    formatNewResult(result) {
      const parts = []
      const add = (label, value) => { if (value !== undefined && value !== 0) parts.push(`${label}: ${value}`) }
      add('Всего', result.total)
      add('успешно', result.success)
      add('ошибок', result.failure)
      add('пропущено', result.skipped)
      add('заблокировано', result.blocked)
      add('без значения', result.no_value)
      return parts.join(', ')
    }
  },
  async mounted() {
    await this.loadLogs();
    const companyInfo = await user.getCompanyInfo();
    this.companyTimezone = companyInfo.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('companyTimezone:', this.companyTimezone);
  },
  beforeUnmount() {
    this.stopLogsRefresh()
  },
  watch: {
    'paginationInfo.size'(newSize, oldSize) {
      if (newSize !== oldSize) {
        this.loadLogs(1, newSize)
      }
    }
  }
}
</script>

<style scoped>
.badge {
  padding: 0.5em 0.75em;
}
</style> 